import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { apiClient } from "../App";
import { 
  LayoutDashboard, 
  Users, 
  Briefcase, 
  CreditCard, 
  BarChart3, 
  LogOut,
  Menu,
  X,
  AlertTriangle,
  Shield,
  BookOpen,
  Key,
  Clock,
  Receipt
} from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";

const navItems = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/clients", label: "Clients", icon: Users },
  { path: "/missions", label: "Missions", icon: Briefcase },
  { path: "/paiements", label: "Paiements", icon: CreditCard },
  { path: "/depenses", label: "Dépenses", icon: Receipt },
  { path: "/relances", label: "Relances", icon: AlertTriangle },
  { path: "/comptabilite", label: "Comptabilité", icon: BookOpen },
  { path: "/conformite-ohada", label: "Conformité OHADA", icon: Shield },
  { path: "/analytics", label: "Analytics", icon: BarChart3 },
  { path: "/utilisateurs", label: "Utilisateurs", icon: Users },
  { path: "/licences", label: "Licences", icon: Key, adminOnly: true },
];

export default function Layout({ children, user, onLogout }) {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [licenceJoursRestants, setLicenceJoursRestants] = useState(null);

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await apiClient.get("/auth/me");
        setUserInfo(response.data);
        
        // Calculer les jours restants si licence d'essai
        if (response.data?.licence) {
          const licence = response.data.licence;
          if (licence.type_licence === "essai" && licence.date_fin) {
            const dateFin = new Date(licence.date_fin);
            const aujourdhui = new Date();
            const diffTime = dateFin.getTime() - aujourdhui.getTime();
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            setLicenceJoursRestants(diffDays > 0 ? diffDays : 0);
          }
        }
      } catch (error) {
        console.error("Erreur récupération info utilisateur:", error);
      }
    };
    fetchUserInfo();
  }, []);

  // Filtrer les items de navigation selon le rôle
  const filteredNavItems = navItems.filter(item => {
    if (item.adminOnly && user?.role !== "super_admin") {
      return false;
    }
    return true;
  });

  return (
    <div className="min-h-screen flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-[#121214] border-r border-zinc-800
        transform transition-transform duration-200 ease-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-zinc-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
                <span className="text-white font-heading font-bold text-lg">ID</span>
              </div>
              <div>
                <h1 className="font-heading font-semibold text-white tracking-tight">IDEAL</h1>
                <p className="text-xs text-zinc-500 uppercase tracking-widest">Consulting</p>
              </div>
            </div>
          </div>

          {/* Licence Trial Badge */}
          {licenceJoursRestants !== null && (
            <div className="px-4 pb-4" data-testid="licence-trial-badge">
              <div className={`p-3 rounded-lg ${
                licenceJoursRestants <= 7 
                  ? 'bg-red-900/30 border border-red-800' 
                  : licenceJoursRestants <= 14 
                    ? 'bg-amber-900/30 border border-amber-800'
                    : 'bg-blue-900/30 border border-blue-800'
              }`}>
                <div className="flex items-center gap-2 mb-1">
                  <Clock className={`w-4 h-4 ${
                    licenceJoursRestants <= 7 
                      ? 'text-red-400' 
                      : licenceJoursRestants <= 14 
                        ? 'text-amber-400'
                        : 'text-blue-400'
                  }`} />
                  <span className={`text-xs uppercase tracking-wider ${
                    licenceJoursRestants <= 7 
                      ? 'text-red-300' 
                      : licenceJoursRestants <= 14 
                        ? 'text-amber-300'
                        : 'text-blue-300'
                  }`}>
                    Licence d'essai
                  </span>
                </div>
                <p className={`text-lg font-bold ${
                  licenceJoursRestants <= 7 
                    ? 'text-red-400' 
                    : licenceJoursRestants <= 14 
                      ? 'text-amber-400'
                      : 'text-blue-400'
                }`}>
                  {licenceJoursRestants} jour{licenceJoursRestants !== 1 ? 's' : ''} restant{licenceJoursRestants !== 1 ? 's' : ''}
                </p>
                {licenceJoursRestants <= 7 && (
                  <p className="text-xs text-red-300/70 mt-1">
                    Renouvelez votre licence
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {filteredNavItems.map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-md
                    transition-colors duration-200
                    ${isActive 
                      ? 'bg-blue-600/20 text-blue-400 border border-blue-600/30' 
                      : 'text-zinc-400 hover:bg-zinc-800/50 hover:text-white'
                    }
                  `}
                >
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-zinc-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center">
                  <span className="text-sm font-medium text-zinc-300">
                    {user?.nom?.charAt(0) || "U"}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-zinc-200 truncate">{user?.nom}</p>
                  <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={onLogout}
                data-testid="logout-btn"
                className="text-zinc-400 hover:text-red-400 hover:bg-red-400/10"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile header */}
        <header className="lg:hidden glass-header sticky top-0 z-30 px-4 py-3">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-md bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
                <span className="text-white font-heading font-bold text-sm">ID</span>
              </div>
              <span className="font-heading font-semibold text-white">IDEAL</span>
            </div>
            <div className="w-10" /> {/* Spacer for centering */}
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-8 overflow-auto">
          <div className="max-w-[1600px] mx-auto animate-fade-in">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
